const API_BASE_URL =
  process.env.REACT_APP_STRAPI_URL || "https://devops.caglayagmur.dev/api";

interface FetchOptions {
  populate?: string | string[];
  filters?: Record<string, any>;
  sort?: string | string[];
  pagination?: {
    page?: number;
    pageSize?: number;
  };
  locale?: string;
}

/**
 * Build query string from options
 */
function buildQueryString(options: FetchOptions = {}): string {
  const params = new URLSearchParams();

  if (options.populate) {
    if (Array.isArray(options.populate)) {
      options.populate.forEach((field) => params.append("populate", field));
    } else {
      params.append("populate", options.populate);
    }
  }

  if (options.filters) {
    Object.entries(options.filters).forEach(([key, value]) => {
      // Handle nested filter objects (like { category: { id: { $eq: 'value' } } })
      if (typeof value === "object" && value !== null) {
        // Check if it's a deeply nested object (relation filter)
        const firstValue = Object.values(value)[0];
        if (typeof firstValue === "object" && firstValue !== null) {
          // Handle relation filters: category: { id: { $eq: value } }
          Object.entries(value).forEach(([nestedKey, nestedValue]) => {
            if (typeof nestedValue === "object" && nestedValue !== null) {
              Object.entries(nestedValue).forEach(
                ([operator, operatorValue]) => {
                  params.append(
                    `filters[${key}][${nestedKey}][${operator}]`,
                    String(operatorValue)
                  );
                }
              );
            }
          });
        } else {
          // Handle simple nested filters: { slug: { $eq: 'value' } }
          Object.entries(value).forEach(([operator, operatorValue]) => {
            params.append(
              `filters[${key}][${operator}]`,
              String(operatorValue)
            );
          });
        }
      } else {
        params.append(`filters[${key}]`, String(value));
      }
    });
  }

  if (options.sort) {
    if (Array.isArray(options.sort)) {
      options.sort.forEach((field) => params.append("sort", field));
    } else {
      params.append("sort", options.sort);
    }
  }

  if (options.pagination) {
    if (options.pagination.page) {
      params.append("pagination[page]", String(options.pagination.page));
    }
    if (options.pagination.pageSize) {
      params.append(
        "pagination[pageSize]",
        String(options.pagination.pageSize)
      );
    }
  }

  if (options.locale) {
    params.append("locale", options.locale);
  }

  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
}

/**
 * Generic fetch wrapper for Strapi API
 */
async function fetchFromStrapi<T>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<T> {
  const queryString = buildQueryString(options);
  const url = `${API_BASE_URL}${endpoint}${queryString}`;

  //console.log("Fetching from URL:", url);

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Failed to fetch from ${endpoint}:`, error);
    throw error;
  }
}

// API Service Methods

export const api = {
  /**
   * Get global settings (site name and nav items)
   */
  getGlobal: async (locale: string = "en"): Promise<any> => {
    return fetchFromStrapi("/api/global", {
      populate: "*",
      locale,
    });
  },

  /**
   * Get all articles
   */
  getArticles: async (
    locale: string = "en",
    options: FetchOptions = {}
  ): Promise<any> => {
    return fetchFromStrapi("/api/articles", {
      populate: ["cover", "category"],
      sort: ["publishedAt:desc"],
      locale,
      ...options,
    });
  },

  /**
   * Get single article by ID
   */
  getArticle: async (id: string, locale: string = "en"): Promise<any> => {
    return fetchFromStrapi(`/api/articles/${id}`, {
      populate: ["cover", "category"],
      locale,
    });
  },

  /**
   * Get article by slug
   */
  getArticleBySlug: async (
    slug: string,
    locale: string = "en"
  ): Promise<any> => {
    return fetchFromStrapi("/api/articles", {
      populate: ["cover", "category"],
      filters: { slug: { $eq: slug } },
      locale,
    });
  },

  /**
   * Get all categories
   */
  getCategories: async (
    locale: string = "en",
    options: FetchOptions = {}
  ): Promise<any> => {
    return fetchFromStrapi("/api/categories", {
      populate: ["articles"],
      locale,
      ...options,
    });
  },

  /**
   * Get single category by ID
   */
  getCategory: async (id: string, locale: string = "en"): Promise<any> => {
    return fetchFromStrapi(`/api/categories/${id}`, {
      populate: ["articles"],
      locale,
    });
  },

  /**
   * Get all projects
   */
  getProjects: async (
    locale: string = "en",
    options: FetchOptions = {}
  ): Promise<any> => {
    return fetchFromStrapi("/api/projects", {
      populate: ["thumbnail"],
      sort: ["publishedAt:desc"],
      locale,
      ...options,
    });
  },

  /**
   * Get single project by slug
   */
  getProjectBySlug: async (
    slug: string,
    locale: string = "en"
  ): Promise<any> => {
    return fetchFromStrapi("/api/projects", {
      populate: ["thumbnail"],
      filters: { slug: { $eq: slug } },
      locale,
    });
  },

  /**
   * Get all resources
   */
  getResources: async (
    locale: string = "en",
    options: FetchOptions = {}
  ): Promise<any> => {
    return fetchFromStrapi("/api/resources", {
      populate: ["category"],
      sort: ["publishedAt:desc"],
      locale,
      ...options,
    });
  },

  /**
   * Get homepage data
   */
  getHomepage: async (locale: string = "en"): Promise<any> => {
    return fetchFromStrapi("/api/homepage", {
      populate: "hero",
      locale,
    });
  },

  /**
   * Get articles page data
   */
  getArticlesPage: async (locale: string = "en"): Promise<any> => {
    return fetchFromStrapi("/api/articles-page", {
      populate: "*",
      locale,
    });
  },

  /**
   * Get about page data
   */
  getAboutPage: async (locale: string = "en"): Promise<any> => {
    return fetchFromStrapi("/api/about-page", {
      populate: "*",
      locale,
    });
  },

  /**
   * Get resources page data
   */
  getResourcesPage: async (locale: string = "en"): Promise<any> => {
    return fetchFromStrapi("/api/resources-page", {
      populate: "*",
      locale,
    });
  },

  /**
   * Get projects page data
   */
  getProjectsPage: async (locale: string = "en"): Promise<any> => {
    return fetchFromStrapi("/api/projects-page", {
      populate: "*",
      locale,
    });
  },
};

export default api;
